/**
 * ================================
 * Consent-aware device storage
 * ================================
 *
 * PECR Regulation 6 and UK/EU GDPR treat writing to localStorage or
 * sessionStorage exactly as they treat cookies: it is storage on the visitor's
 * terminal equipment, and it needs consent unless strictly necessary.
 *
 * OneTrust Auto-Blocking only governs whether a <script> executes. Anything the
 * theme's own bundle writes therefore sits entirely outside OneTrust's reach and
 * has to be gated here instead. That is how ad click identifiers (dr_gclid,
 * dr_fbaid, dr_licid) were still being persisted for visitors who had refused
 * consent, even after the cookie-side remediation of 2026-08-17.
 *
 * Behaviour:
 *   - Consent already given for the category  -> write immediately.
 *   - Consent refused                         -> never write.
 *   - Consent not yet decided                 -> hold the write and flush it if
 *                                                and when the category becomes
 *                                                active.
 *
 * Holding rather than dropping matters because OneTrust resolves asynchronously
 * (it needs a geolocation lookup), so on a first page view these writes are
 * usually attempted before any consent state exists. Treating "unknown" as
 * "denied" is the safe default; the queue means a visitor who then accepts is
 * not silently missing attribution.
 *
 * In-memory values are deliberately NOT gated. A JavaScript variable holding a
 * UTM parsed from the current URL is transient page state, not storage on the
 * device, so same-page form attribution keeps working without consent.
 */

const pending = [];
let watching = false;
let pollsRemaining = 20; // ~10s at 500ms, covers a slow OneTrust geolocation lookup

/**
 * Is the given OneTrust category currently active for this visitor?
 *
 * window.OptanonActiveGroups is a comma-delimited string with leading and
 * trailing commas, e.g. ",C0001,C0003,". It is undefined until OneTrust has
 * resolved, which we treat as "no consent".
 *
 * @param {string} category OneTrust category id, e.g. 'C0004'.
 * @return {boolean}
 */
export function isCategoryAllowed(category) {
    const groups = window.OptanonActiveGroups;

    if (typeof groups !== 'string' || groups === '') {
        return false;
    }

    return groups.indexOf(',' + category + ',') !== -1;
}

/**
 * Write any held items whose category has since become active.
 * Items for categories the visitor refused stay queued and are never written.
 */
function flushPending() {
    for (let i = pending.length - 1; i >= 0; i--) {
        const item = pending[i];

        if (isCategoryAllowed(item.category)) {
            try {
                item.store.setItem(item.key, item.value);
            } catch (e) {
                // Storage disabled or full. Nothing useful to do.
            }
            pending.splice(i, 1);
        }
    }
}

/**
 * Watch for consent becoming available.
 *
 * OneTrust dispatches OneTrustGroupsUpdated on window when consent is first
 * established and on every later change. The bounded poll is a belt-and-braces
 * fallback so this keeps working if that event name changes between SDK
 * versions; it stops as soon as consent resolves or the budget runs out.
 */
function watchForConsent() {
    if (watching) {
        return;
    }
    watching = true;

    window.addEventListener('OneTrustGroupsUpdated', flushPending);

    const poll = window.setInterval(function () {
        pollsRemaining--;

        if (typeof window.OptanonActiveGroups === 'string' && window.OptanonActiveGroups !== '') {
            flushPending();
            window.clearInterval(poll);
            return;
        }

        if (pollsRemaining <= 0) {
            window.clearInterval(poll);
        }
    }, 500);
}

/**
 * Persist a value only if the visitor has consented to the given category.
 *
 * @param {Storage} store    window.localStorage or window.sessionStorage.
 * @param {string}  key      Storage key.
 * @param {string}  value    Value to persist.
 * @param {string}  category OneTrust category id gating this write.
 * @return {boolean} true if written now, false if held or refused.
 */
export function setItemWithConsent(store, key, value, category) {
    if (isCategoryAllowed(category)) {
        try {
            store.setItem(key, value);
        } catch (e) {
            // Storage disabled or full.
        }
        return true;
    }

    pending.push({ store: store, key: key, value: value, category: category });
    watchForConsent();

    return false;
}
